import { GetOffersBySearchQuery_offers } from '@frontend/applications/AffiliatesApp/queries/types/GetOffersBySearchQuery';
import * as React from 'react';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { format } from 'date-fns';
import LinkSimpleIcon from '@revfluence/fresh-icons/regular/esm/LinkSimpleIcon';
import { TvIcon } from '@revfluence/fresh-icons/regular';
import { ShieldCheckIcon } from '@revfluence/fresh-icons/solid/esm';
import Tag from '@frontend/app/refresh-components/Tag';
import { ShopifyIcon } from '@revfluence/fresh-icons/brands/esm';
import {
  getPayoutLabel,
  getUniqueRefreshTagColor,
  getUsageLimitMessage,
  getDiscountCombinationMessage,
} from '@frontend/applications/AffiliatesApp/components/OfferForm/utils/OfferFormUtils';
import {
  OFFER_PRICE_RULE_TYPE,
  OFFER_PROMO_USAGE_LIMIT_RULE,
  OFFER_SOURCE,
  OFFER_TYPE,
} from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { RefreshOfferDefaultImage } from '@frontend/applications/AffiliatesApp/components/OfferImage/RefreshOfferDefaultImage';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@frontend/shadcn/components/ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faChevronDown, faChevronUp, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { Button } from '@frontend/shadcn/components/ui/button';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { SALES_APP_ID } from '@frontend/app/constants/applicationIds';
import { ConversionTrackingModal } from '@frontend/applications/AffiliatesApp/components';

// Types
interface OfferCardProps {
  offer: GetOffersBySearchQuery_offers;
  isExpanded?: boolean;
  onConnectOffer: (offerType: OFFER_TYPE, offerConnectMode: 'connect' | 'update') => void;
  onDisconnectOffer: (offerId: number) => Promise<void>;
}

interface UrlDisplayProps {
  url: string;
}

interface OfferHeaderProps {
  offer: GetOffersBySearchQuery_offers;
  source: OFFER_SOURCE;
  discountLabel: string;
  isMultipleShopifySyncEnabled: boolean;
  expanded: boolean;
  onExpandToggle: () => void;
  onConnectOffer: (offerType: OFFER_TYPE, offerConnectMode: 'connect' | 'update') => void;
  onDisconnectOffer: (offerId: number) => Promise<void>;
}

interface OfferCommissionsProps {
  payouts: GetOffersBySearchQuery_offers['payouts'];
}

interface OfferFeaturesProps {
  isPromoLink: boolean;
  isLandingPageEnabled: boolean;
  isSecureCodes?: boolean;
  linkOffer?: GetOffersBySearchQuery_offers['links'][0];
  source: OFFER_SOURCE;
}

interface OfferDetailsProps {
  details: string[];
}

// Helper Components
const UrlDisplay: React.FC<UrlDisplayProps> = ({ url }) => {
  if (!url) return null;
  const displayUrl = url.length > 70 ? `${url.substring(0, 70)}...` : url;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-sm text-primary font-medium underline m-0 break-all cursor-pointer">{displayUrl}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[500px] break-all text-center bg-grey-6 text-grey-0 p-[6px] rounded-lg">
          <span>{url}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const OfferHeader: React.FC<OfferHeaderProps> = ({
  offer,
  source,
  discountLabel,
  isMultipleShopifySyncEnabled,
  expanded,
  onExpandToggle,
  onConnectOffer,
  onDisconnectOffer,
}) => {
  const history = useHistory();
  const location = useLocation();
  const linkOffer = offer.links[0];
  const [instructionsVisible, setInstructionsVisible] = React.useState(false);
  const handleViewInSalesTracking = () => {
    history.push({
      ...location,
      pathname: `/app/${SALES_APP_ID}/offers/${offer.id}/details`,
    });
  };

  const handleChangeOffer = () => {
    onConnectOffer(offer.type, 'update');
  };

  const handleDisconnectOffer = async () => {
    await onDisconnectOffer(offer.id);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <RefreshOfferDefaultImage
            imageUrl={offer.imageUrl}
            source={source}
            icon={offer.icon}
            iconClassName="h-6 w-6"
            className="w-16 h-16"
          />
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex justify-between">
              <p className="text-sm text-grey-6 font-normal m-0">{offer.name}</p>
              <div className="flex items-center gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Open menu">
                      <FontAwesomeIcon icon={faEllipsis} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem className="text-sm" onClick={handleViewInSalesTracking}>
                      View in Sales Tracking
                    </DropdownMenuItem>
                    {source === OFFER_SOURCE.TUNE && (
                      <DropdownMenuItem className="text-sm" onClick={() => setInstructionsVisible(true)}>
                        View setup instructions
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="text-sm" onClick={handleChangeOffer}>
                      Change offer
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-sm text-red-600 focus:bg-red-50" onClick={handleDisconnectOffer}>
                      Remove from campaign
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button onClick={onExpandToggle} className="p-1 hover:bg-grey-1 rounded-full">
                  {expanded ? (
                    <FontAwesomeIcon icon={faChevronUp} className="h-4 w-4 text-grey-4" />
                  ) : (
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-grey-4" />
                  )}
                </button>
              </div>
            </div>
            {source === OFFER_SOURCE.SHOPIFY ? (
              <p className="text-[20px] text-primary font-medium m-0">{discountLabel}</p>
            ) : (
              <UrlDisplay url={linkOffer?.url} />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {isMultipleShopifySyncEnabled && (
            <Tag
              bgColor="bg-grey-6"
              textColor="text-grey-0"
              className="rounded-lg"
              label={
                <span className="flex items-center gap-1">
                  <ShopifyIcon /> Promo Code Sync Enabled
                </span>
              }
            />
          )}
          <div className="flex items-center gap-1">
            <FontAwesomeIcon icon={faCalendar} className="h-4 w-4 text-green-4" />
            <p className="text-xs text-grey-6 font-medium m-0">
              Created on {format(new Date(offer.createdDate.toString()), 'MMM dd, yyyy')}
            </p>
          </div>
        </div>
      </div>
      {
        source === OFFER_SOURCE.TUNE ? (
          <ConversionTrackingModal
            offerTrackingType={linkOffer.conversionTrackingType}
            onCancel={() => setInstructionsVisible(false)}
            pixelCode={linkOffer.pixelCode}
            postbackUrl={linkOffer.postbackUrl}
            visible={instructionsVisible}
          />
        ) : null
      }
    </>
  );
};

const OfferCommissions: React.FC<OfferCommissionsProps> = ({ payouts }) => {
  if (!payouts?.length) return null;

  return (
    <div className="border-b border-grey-2 py-3">
      <p className="m-0 text-xs text-primary font-medium">Commissions</p>
      <div className="flex gap-2 flex-wrap">
        {payouts.map((payout, index) => {
          const label = getPayoutLabel(payout);
          if (!label) return null;

          return (
            <div
              key={index}
              className={`${getUniqueRefreshTagColor(index)} text-grey-0 rounded-lg h-[22px] px-2 w-max`}
            >
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OfferFeatures: React.FC<OfferFeaturesProps> = ({
  isPromoLink,
  isLandingPageEnabled,
  isSecureCodes,
  linkOffer,
  source,
}) => {
  if (!isPromoLink && !isLandingPageEnabled && !isSecureCodes && !linkOffer) return null;

  return (
    <div className="border-b border-grey-2 py-3">
      {isPromoLink && (
        <div className="flex items-center gap-2">
          <LinkSimpleIcon fontSize={16} className="text-magenta-4" />
          <p className="text-xs font-medium text-primary m-0">Creators will receive a shareable link</p>
        </div>
      )}
      {isLandingPageEnabled && (
        <div className="flex items-center gap-2">
          <TvIcon fontSize={16} className="text-blue-4" />
          <p className="text-xs font-medium text-primary m-0">Personalized CreatorStore</p>
        </div>
      )}
      {isSecureCodes && (
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="text-green-4 h-4 w-8" />
          <p className="text-xs font-medium text-primary m-0">
            A single-use promo code with a string of random digits for security will be generated when customers click
            the link.
          </p>
        </div>
      )}
      {source === OFFER_SOURCE.TUNE && linkOffer && (
        <>
          <div className="flex items-center gap-2">
            <LinkSimpleIcon fontSize={16} className="text-magenta-4" />
            <p className="text-xs font-medium text-primary m-0">
              {isPromoLink ? 'Creators will receive a shareable link' : 'Shareable Link'}
            </p>
          </div>
          {linkOffer.creatorDeeplink && (
            <p className="text-xs text-primary ml-6">Creators are allowed to create custom deeplinks.</p>
          )}
          {linkOffer.domains && linkOffer.domains.length > 0 && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="multipleDomain" className="border-0">
                <AccordionTrigger className="text-primary p-0 font-normal text-xs hover:no-underline">
                  <li>Other supported domains</li>
                </AccordionTrigger>
                <AccordionContent className="pl-5 text-grey-700">
                  <ul className="list-disc list-inside pl-4 text-xs">
                    {linkOffer.domains.map((domain, index) => (
                      <li key={index}>{domain}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </>
      )}
    </div>
  );
};

const OfferDetails: React.FC<OfferDetailsProps> = ({ details }) => {
  if (!details.length) return null;

  return (
    <div className="py-3">
      <p className="text-sm text-primary font-medium m-0">Offer Details</p>
      <ul className="list-disc list-inside text-primary text-sm font-normal">
        {details.map((detail, i) => (
          <li key={i} className="text-primary text-sm font-normal">
            {detail}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Main Component
export const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  isExpanded = false,
  onConnectOffer,
  onDisconnectOffer,
}) => {
  const [expanded, setExpanded] = React.useState(isExpanded);
  const promo = offer.promos[0];
  const isPromoLink = offer.isPromoLink;
  const linkOffer = offer.links[0];
  const source = promo ? OFFER_SOURCE.SHOPIFY : OFFER_SOURCE.TUNE;
  const isMultipleShopifySyncEnabled = promo?.connectedClientMetadata?.some((client) => client.status === 'ACTIVE');

  // Calculate discount label
  const discountLabel = React.useMemo(() => {
    if (!promo) return '';

    if (promo.priceRuleType === OFFER_PRICE_RULE_TYPE.AMOUNT) {
      return `${promo.priceRuleAmount ? `$${promo.priceRuleAmount}` : ''} Discount`;
    }
    if (promo.priceRuleType === OFFER_PRICE_RULE_TYPE.PERCENTAGE) {
      return `${promo.priceRuleAmount ?? ''}% Discount`;
    }
    return '';
  }, [promo]);

  // Calculate offer details
  const offerDetails = React.useMemo(() => {
    const details: string[] = [];

    if (promo) {
      if (promo.isOneTime && promo.isSubscription) details.push('Applies to one-time purchase');
      else if (promo.isOneTime) details.push('Applies to one-time purchase');
      else if (promo.isSubscription) {
        details.push('Applies to subscription purchase');
        if (promo.recurringCycleLimit === 0) {
          details.push('For all recurring payments');
        } else if (promo.recurringCycleLimit === 1) {
          details.push('Limited to 1 recurring payment');
        } else {
          details.push(`Limited to ${promo.recurringCycleLimit} recurring payments`);
        }
      }

      if (promo.usageLimitRule === OFFER_PROMO_USAGE_LIMIT_RULE.NONE) {
        details.push('No minimum purchase requirements');
      }
      if (promo.usageLimitRule === OFFER_PROMO_USAGE_LIMIT_RULE.MINIMUM_PURCHASE_AMOUNT) {
        details.push(`Minimum purchase of $${Number(promo.usageLimitAmount).toFixed(2)}`);
      }
      if (promo.usageLimitRule === OFFER_PROMO_USAGE_LIMIT_RULE.MINIMUM_ITEM_AMOUNT) {
        details.push(`Minimum purchase of ${promo.usageLimitAmount} items`);
      }

      details.push(
        getUsageLimitMessage(
          promo.specialLimitOnePerSale,
          promo.specialLimitUsageCapEnabled,
          promo.specialLimitNewCustomersOnly,
          promo.specialLimitUsageCapAmount?.toString(),
        ),
      );

      details.push(
        getDiscountCombinationMessage(
          promo.providerMetadata?.discountCombination?.productDiscounts,
          promo.providerMetadata?.discountCombination?.orderDiscounts,
          promo.providerMetadata?.discountCombination?.shippingDiscounts,
        ),
      );

      if (promo?.providerMetadata?.productCollections?.length > 0) {
        details.push('Applies to specific collections');
      }

      if (promo.startDate) {
        details.push(`Active from ${format(new Date(promo.startDate), 'MMM dd, yyyy')}`);
        if (promo.endDate) {
          details[details.length - 1] += ` until ${format(new Date(promo.endDate), 'MMM dd, yyyy')}`;
        }
      } else {
        details.push(`Active from ${format(new Date(offer.createdDate), 'MMM dd, yyyy')}`);
      }
    } else if (linkOffer) {
      if (offer.expirationDate) {
        details.push(`Offer ends on ${format(new Date(offer.expirationDate), 'MMM dd, yyyy')}`);
      }
      if (linkOffer.creatorDeeplink) {
        details.push('Creators are allowed to create custom deeplinks.');
      }
    }

    return details;
  }, [promo, linkOffer, offer]);

  return (
    <Card className="p-6">
      <CardContent className="p-0 flex flex-col">
        <OfferHeader
          offer={offer}
          source={source}
          discountLabel={discountLabel}
          isMultipleShopifySyncEnabled={isMultipleShopifySyncEnabled}
          expanded={expanded}
          onExpandToggle={() => setExpanded(!expanded)}
          onConnectOffer={onConnectOffer}
          onDisconnectOffer={onDisconnectOffer}
        />

        {expanded && (
          <>
            <OfferCommissions payouts={offer.payouts} />
            <OfferFeatures
              isPromoLink={isPromoLink}
              isLandingPageEnabled={offer.isLandingPageEnabled}
              isSecureCodes={promo?.isSecureCodes}
              linkOffer={linkOffer}
              source={source}
            />
            <OfferDetails details={offerDetails} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

OfferCard.displayName = 'OfferCard';
